import type { components } from '@/lib/api/generated/schema'
import { api } from '@/lib/api'

type Schemas = components['schemas']

export type AdminDashboard = Schemas['DashboardResponse']
export type AdminDashboardKpis = Schemas['DashboardKpis']
export type AdminUserListItem = Schemas['AdminUserListItem']
export type AdminUserListResponse = Schemas['AdminUserListResponse']
export type AdminActivityListItem = Schemas['AdminActivityListItem']
export type AdminActivityListResponse = Schemas['AdminActivityListResponse']
export type AdminActivityDetail = Schemas['AdminActivityDetailResponse']
export type AdminActivityParticipant = Schemas['AdminActivityParticipant']

export type AdminDashboardDays = 7 | 30 | 90
export type AdminRecordKind = 'users' | 'activities'

export interface AdminRecordFilters {
  status?: string
  verification?: string
  createdDays?: string
  period?: string
  pendingRequests?: string
}

interface RequestOptions {
  signal?: AbortSignal
}

/**
 * Read-only admin endpoints (D-055). The in-app dashboard never mutates data:
 * moderation actions remain in the protected admin console.
 */
export const adminApi = {
  getDashboard(
    days: AdminDashboardDays,
    options: RequestOptions = {},
  ): Promise<AdminDashboard> {
    return api.get<AdminDashboard>('/admin/dashboard', {
      query: { days: String(days) },
      signal: options.signal,
    })
  },

  listUsers(
    params: {
      query?: string
      filters?: AdminRecordFilters
      page?: number
    } = {},
    options: RequestOptions = {},
  ): Promise<AdminUserListResponse> {
    return api.get<AdminUserListResponse>('/admin/users', {
      query: {
        ...params.filters,
        ...(params.query?.trim() ? { q: params.query.trim() } : {}),
        page: String(params.page ?? 1),
      },
      signal: options.signal,
    })
  },

  listActivities(
    params: {
      query?: string
      filters?: AdminRecordFilters
      page?: number
    } = {},
    options: RequestOptions = {},
  ): Promise<AdminActivityListResponse> {
    return api.get<AdminActivityListResponse>('/admin/activities', {
      query: {
        ...params.filters,
        ...(params.query?.trim() ? { q: params.query.trim() } : {}),
        page: String(params.page ?? 1),
      },
      signal: options.signal,
    })
  },

  getActivity(
    activityId: string,
    options: RequestOptions = {},
  ): Promise<AdminActivityDetail> {
    return api.get<AdminActivityDetail>(
      `/admin/activities/${encodeURIComponent(activityId)}`,
      { signal: options.signal },
    )
  },
}
